import Promise from 'bluebird';
import { some, pick } from 'underscore';
import { getLanguage, getDefaultLanguage, languageParseMode } from './countdown-language';
import { i18nDummy } from './i18n-dummy';
import { defaultCountdownDays } from './utils';

/**
 * Task queue for substituting countdown rule
 *   definitions with their evaluated values
 */
let countdownEvalTaskChain = Promise.resolve('');

/*
 * Captures:
 * [0]: whole match
 * [1]: function name
 * [2]: date
 * [3]: time (may be undefined)
 * [4]: locale (may be undefined)
 * [5]: number of days to countdown from (may be undefined)
 */
const countdownRuleSyntax = /\{=\s*(global_countdown|countdown)\s*\(\s*"(?:(\d{4}\/\d{1,2}\/\d{1,2})(?:\s((?:[0-1][0-9]|2[0-3]):(?:[0-5][0-9]):(?:[0-5][0-9]))|))"\s*(?:,\s*"([a-zA-Z]{2}(?:-[a-zA-Z\d]{2,3}|))"\s*(?:,\s*([1-9]\d{0,2})|)|)\s*\)\s*\}/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: function name
 * [2]: date
 * [3]: time (may be undefined)
 * [4]: number of days to countdown from (may be undefined)
 */
const RSACountdownRuleSyntax = /\{\s*(global_countdown|countdown)\s*\(\s*(?:(\d{4}-\d{1,2}-\d{1,2})(?:\s((?:[0-1][0-9]|2[0-3]):(?:[0-5][0-9]):(?:[0-5][0-9]))|))\s*(?:,\s*([1-9]\d{0,2})|)\s*\)\s*\}/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: first letter of function name
 */
const countdownRuleInProgressSyntax = /\{=\s*(c|g)[a-z]*(?:\s*\([a-z0-9/":\s,-]*(?:\)\s*|)|)/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: first letter of function name
 */
const RSACountdownRuleInProgressSyntax = /\{\s*(c(?:o|$)(?:u|$)|g(?:l|$)(?:o|$))[a-z_]*(?:\s*\([a-z0-9/":\s,-]*(?:\)\s*|)|)/i;

/**
 * Produces "<n> days" in current locale
 * @param {int} days - number of days
 * @param {Object} i18n - i18n in a target countdown locale
 * @returns {string} "<n> days" in current locale
 */
function translateDays(days, i18n) {
  const translation = i18n.formatUnit(days, 'day');

  return translation;
}

/**
 * Looks for a countdown rule time definition part in given text starting from passed index.
 *   If found, it parses the definition.
 * @param {string} text - text to search in
 * @param {int} startIndex - starts searching at this index
 * @param {int} parseMode - flag for whether we are parsing for preview, editor or character count
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseCountdownRule({
  text, startIndex, cursorIndex, parseMode, i18n, adType, permissions = {},
}) {
  const input = text ? text.substring(startIndex) : '';

  if (adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') {
    const tokens = RSACountdownRuleSyntax.exec(input);

    if ((tokens !== null) &&
      (tokens.length === 5) &&
      !Number.isNaN(Date.parse(tokens[2])) &&
      (!cursorIndex || (cursorIndex > (startIndex + tokens.index)))) {
      return {
        source: tokens[0],
        isComplete: true,
        type: tokens[1].toLowerCase(),
        date: tokens[2],
        time: tokens[3],
        days: tokens[4] ? parseInt(tokens[4], 10) : defaultCountdownDays,
        language: getDefaultLanguage(i18n, permissions),
        includeAdCustomizerFeed: false,
      };
    }
  } else {
    const tokens = countdownRuleSyntax.exec(input);

    // first try to parse countdown rule without adcustomizer
    // Not an acceptable rule if syntax is invalid, date is invalid, or has an unsupported language
    if ((tokens !== null) &&
      (tokens.length === 6) &&
      !Number.isNaN(Date.parse(tokens[2])) &&
      (!cursorIndex || (cursorIndex > (startIndex + tokens.index)))) {
      const language = getLanguage(tokens[4], parseMode, i18n, permissions);

      if (language) {
        return {
          source: tokens[0],
          isComplete: true,
          type: tokens[1].toLowerCase(),
          date: tokens[2],
          time: tokens[3],
          language,
          days: tokens[5] ? parseInt(tokens[5], 10) : defaultCountdownDays,
          includeAdCustomizerFeed: false,
        };
      }
    }
  }

  return null;
}


/**
 * Looks for an incomplete countdown rule definition in given text
 *   starting from passed index. If found, it returns countdown type.
 * @param {Object} options needed
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseCountdownRuleInProgress({
  text, startIndex, cursorIndex, i18n, adType, permissions = {},
}) {
  const input = text.substring(startIndex);
  if (adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') {
    const tokens = RSACountdownRuleInProgressSyntax.exec(input);

    if ((tokens === null) ||
      (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
      return null;
    }

    if (tokens.length !== 2 ||
      (!tokens[1].startsWith('c') && !tokens[1].startsWith('g'))) {
      return null;
    }

    return {
      source: tokens[0],
      type: tokens[1] === 'countdown(' || tokens[1].startsWith('c') ?
        'countdown' :
        'global_countdown',
      days: defaultCountdownDays,
    };
  }
  const tokens = countdownRuleInProgressSyntax.exec(input);

  if ((tokens === null) ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  if ((tokens.length !== 2) ||
    ((tokens[1] !== 'c') && (tokens[1] !== 'g'))) {
    return null;
  }

  return {
    source: tokens[0],
    type: tokens[1] === 'countdown(' || tokens[1] === 'c' ?
      'countdown' :
      'global_countdown',
    language: getDefaultLanguage(i18n, permissions),
    days: defaultCountdownDays,
  };
}

/**
 * Looks for countdown rule definitions in given text
 *   and substitutes them with their evaluated and translated values.
 * @param {string} text - input text
 * @param {Object} i18n - i18n which has formatUnit function
 * @returns {object} a promise that resolves to a modified version of input text
 *   where all valid countdown rules
 * are substituted with their evaluated values
 */
export function evaluateCountdownRules({
  text, i18n, adType, permissions = {},
}) {
  let processedText = text;

  function queueSubstitution(rule) {
    countdownEvalTaskChain = countdownEvalTaskChain.then((lang) => {
      const currentLanguage = lang.toLowerCase();

      if (rule.language.locale === currentLanguage) {
        const translation = translateDays(rule.days, i18nDummy);

        processedText = processedText.replace(rule.source, translation);
        return Promise.resolve(rule.language.locale);
      }
      return i18nDummy.setLocale(rule.language.locale).then(() => {
        const translation = translateDays(rule.days, i18nDummy);

        processedText = processedText.replace(rule.source, translation);
        return rule.language.locale;
      });
    });
  }

  let searchStartIndex = 0;
  let rule = parseCountdownRule({
    text,
    startIndex: searchStartIndex,
    parseMode: languageParseMode.ForPreview,
    i18n,
    permissions,
    adType,
  });

  // We need to use this global task chain to avoid interrupting
  //  ongoing get/set locale requests.
  // Each evaluation cycle begins with getting current locale and then
  //  for each countdown rule found,
  // we queue a task to set locale (if needed) and do substitution.
  if (rule !== null) {
    countdownEvalTaskChain = countdownEvalTaskChain.then(() => i18nDummy.getLocale());

    while (rule !== null) {
      queueSubstitution(rule);
      searchStartIndex += rule.source.length;
      rule = parseCountdownRule({
        text,
        startIndex: searchStartIndex,
        parseMode: languageParseMode.ForPreview,
        i18n,
        permissions,
        adType,
      });
    }
  }

  return countdownEvalTaskChain.then(() => processedText);
}

/**
 * Parses out properties to initialize a countdown helper using current cursor position
 * @param {string} text - input text
 * @param {int} cursorIndex - current cursor position
 * @param {Object} i18n - i18n which has formatUnit function
 * @returns {Object} properties to initialize a countdown helper
 *   if cursor is placed within a valid definition or at the end of a partial definition.
 * Returns undefined; otherwise.
 */
export function parseRule({
  text, cursorIndex, i18n, adType, permissions = {},
}) {
  const startIndex = text.lastIndexOf('{', cursorIndex);

  if (startIndex === -1 || cursorIndex === startIndex) {
    return null;
  }

  const parsers = [
    {
      // Check if cursor is placed in the middle of a valid syntax
      parse() {
        return parseCountdownRule({
          text,
          startIndex,
          parseMode: languageParseMode.ForEditor,
          cursorIndex,
          i18n,
          permissions,
          adType,
        });
      },
      keys: ['type', 'date', 'time', 'language', 'days', 'includeAdCustomizerFeed', 'feed', 'attributeWithOthers', 'isComplete', 'source'],
      inclusive: false,
    },
    {
      // Check if we are, instead, just starting to type a countdown rule
      parse() {
        return parseCountdownRuleInProgress({
          text,
          startIndex,
          cursorIndex,
          i18n,
          permissions,
          adType,
        });
      },
      keys: ['type', 'language', 'days', 'includeAdCustomizerFeed', 'source'],
      inclusive: true,
    },
  ];

  let properties = null;

  some(parsers, (parser) => {
    const rule = parser.parse();

    if ((rule !== null) &&
      (cursorIndex < startIndex + rule.source.length + (parser.inclusive ? 1 : 0))) {
      const options = pick(rule, parser.keys);

      options.startIndex = startIndex;
      options.endIndex = startIndex + rule.source.length;

      properties = {
        type: 'countdown',
        options,
      };

      return true;
    }
    return false;
  });

  return properties;
}
